// =============================================================================
// Session Timeout Hook
// Implements automatic logout after inactivity with warning popup
// =============================================================================

import { useState, useEffect, useCallback, useRef } from 'react';

interface UseSessionTimeoutOptions {
  // Total timeout in milliseconds (default: 15 minutes)
  timeout?: number;
  // Warning time before logout in milliseconds (default: 5 minutes)
  warningTime?: number;
  // Callback when session expires
  onTimeout: () => void;
  // Optional callback when user extends session (for token refresh)
  onExtend?: () => Promise<void>;
}

interface UseSessionTimeoutReturn {
  // Whether the warning modal should be shown
  showWarning: boolean;
  // Time remaining before logout (in seconds)
  timeRemaining: number;
  // Dismiss warning and reset timer
  extendSession: () => void;
}

const ACTIVITY_EVENTS = [
  'mousedown',
  'mousemove',
  'keydown',
  'scroll',
  'touchstart',
  'click',
];

export function useSessionTimeout({
  timeout = 15 * 60 * 1000, // 15 minutes
  warningTime = 5 * 60 * 1000, // 5 minutes
  onTimeout,
  onExtend,
}: UseSessionTimeoutOptions): UseSessionTimeoutReturn {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);

  // Use refs to avoid stale closures in event handlers
  const showWarningRef = useRef(showWarning);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const onTimeoutRef = useRef(onTimeout);

  // Keep refs in sync
  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  // Reset the session timer
  const resetTimer = useCallback(() => {
    clearTimers();
    setShowWarning(false);
    setTimeRemaining(0);

    // Set warning timeout (total timeout - warning time = time before warning shows)
    const warningDelay = timeout - warningTime;
    warningTimeoutRef.current = setTimeout(() => {
      setShowWarning(true);
      setTimeRemaining(Math.floor(warningTime / 1000));

      // Start countdown
      countdownRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearTimers();
            onTimeoutRef.current();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, warningDelay);

    // Set final timeout as backup
    timeoutRef.current = setTimeout(() => {
      clearTimers();
      onTimeoutRef.current();
    }, timeout);
  }, [clearTimers, timeout, warningTime]);

  // Extend session (user clicked "I'm still here")
  const extendSession = useCallback(async () => {
    // Call onExtend callback first to refresh the backend token
    if (onExtend) {
      try {
        await onExtend();
      } catch (error) {
        console.error('Failed to extend session:', error);
        // If token refresh fails, trigger logout
        onTimeoutRef.current();
        return;
      }
    }
    // Reset the frontend timer
    resetTimer();
  }, [resetTimer, onExtend]);

  // Set up activity tracking - runs only once on mount
  useEffect(() => {
    // Throttle activity tracking
    let lastActivity = Date.now();
    const THROTTLE_MS = 1000;

    const handleActivity = () => {
      const now = Date.now();
      // Throttle: only process if enough time has passed
      if (now - lastActivity < THROTTLE_MS) {
        return;
      }
      lastActivity = now;

      // Only reset if warning is NOT showing (use ref for current value)
      if (!showWarningRef.current) {
        resetTimer();
      }
    };

    // Add event listeners
    ACTIVITY_EVENTS.forEach((event) => {
      document.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    resetTimer();

    // Cleanup on unmount
    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        document.removeEventListener(event, handleActivity);
      });
      clearTimers();
    };
  }, [resetTimer, clearTimers]);

  return {
    showWarning,
    timeRemaining,
    extendSession,
  };
}

export default useSessionTimeout;
